import { useState, useMemo, type ReactNode } from 'react';

import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import { useGetExternalTools, useGetExternalToolCategories } from '@/entities/external-tool';
import { useCurrentWorkspaceId } from '@/entities/workspace';

import { ExternalToolsEmptyState, ExternalToolsFilter, ExternalToolsGrid } from './components';
import { useExternalToolsHeaderConfig } from '../model/useExternalToolsHeaderConfig';
import type {
  ExternalToolsWidgetProps,
  ExternalToolsWidgetSpringboardDeps,
  ExternalToolsWidgetUseSpringboardToolsReturn,
} from '../model/external-tools-widget.types';
import { CardGridSkeleton } from '@/shared/ui';
import { useTranslation } from 'react-i18next';

// dependency injection — `useSpringboardTools` + pre-resolved tabId / externalToolAgentId
// injected by the config-layer factory (`@/lib/config/agents.config`). When
// no `springboardDeps` is supplied (bare render / test that doesn't
// exercise the springboard integration), use a no-op hook so the widget
// renders without crashing.
const noopSpringboardTools = (): ExternalToolsWidgetUseSpringboardToolsReturn => ({ tools: [] });
const noopSpringboardDeps: ExternalToolsWidgetSpringboardDeps = {
  useSpringboardTools: noopSpringboardTools,
  tabId: '',
  externalToolAgentId: '',
};

export function ExternalToolsWidget(props: ExternalToolsWidgetProps) {
  const { t } = useTranslation('admin');

  const { data, springboardDeps = noopSpringboardDeps } = props;
  const workspaceId = useCurrentWorkspaceId();
  const { data: externalTools, isLoading } = useGetExternalTools({
    initialData: data.items,
    workspaceId: workspaceId ?? undefined,
    enabled: !!workspaceId,
  });
  const { data: categories = [] } = useGetExternalToolCategories({
    workspaceId: workspaceId ?? undefined,
    enabled: !!workspaceId,
  });
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // dependency injection — `useSpringboardTools` injected by the config-layer factory
  // wrapping this widget (see `@/lib/config/agents.config`). The wrapper
  // pre-resolves the springboard `tabId` constant + the special-agent id
  // for the External Tools agent so the widget itself never imports
  // cross-feature constants.
  const { tools } = springboardDeps.useSpringboardTools({
    tabId: springboardDeps.tabId,
    enabled: true,
  });

  const externalToolInfo = tools.find((tool) => tool.id === springboardDeps.externalToolAgentId);

  const headerConfig = useExternalToolsHeaderConfig({
    title: externalToolInfo?.title,
  });

  const filteredTools = useMemo(() => {
    if (!externalTools) {
      return [];
    }
    if (selectedCategory) {
      return externalTools.filter((t) => t.categories.some((c) => c.id === selectedCategory));
    }
    return externalTools;
  }, [externalTools, selectedCategory]);

  // eslint-disable-next-line i18next/no-literal-string -- scroll restoration key discriminator
  const scrollKey = `external-tools-${selectedCategory || 'all'}`;

  const selectedCategoryName = useMemo(() => {
    if (!selectedCategory) {
      return undefined;
    }
    return categories.find((c) => c.id === selectedCategory)?.title;
  }, [selectedCategory, categories]);

  // single call site, pure branch dispatch — inlined as a plain computed
  // value rather than a `renderExternalTools`-style closure.
  const externalToolsContent: ReactNode = isLoading ? (
    <CardGridSkeleton count={4} />
  ) : filteredTools.length > 0 ? (
    <ExternalToolsGrid externalTools={filteredTools} />
  ) : (
    <ExternalToolsEmptyState categoryName={selectedCategoryName} />
  );

  return (
    <SidePanel headerConfig={headerConfig} scrollKey={scrollKey}>
      <div className="flex h-full flex-col gap-6 px-10 py-4">
        <ExternalToolsFilter
          title={externalToolInfo?.title || t('externalToolsWidget.externalTools')}
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
        {externalToolsContent}
      </div>
    </SidePanel>
  );
}
